import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Calendar, 
  Truck, 
  CreditCard, 
  Download, 
  ChevronDown, 
  ChevronUp, 
  ExternalLink,
  Loader2 
} from 'lucide-react';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const MyOrders = () => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setError("Please login to view orders");
        return;
      }

      let ordersQuery = query(
        collection(db, "orders"),
        where("userId", "==", userId),
        orderBy("orderDate", "desc")
      );

      if (filter !== 'all') {
        ordersQuery = query(
          collection(db, "orders"),
          where("userId", "==", userId),
          where("status", "==", filter),
          orderBy("orderDate", "desc")
        );
      }

      const querySnapshot = await getDocs(ordersQuery);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrders(ordersData);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const generateReceipt = async (order) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]); // A4 size
      const { width, height } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Header
      page.drawText('Ambaari Jewels', {
        x: 50,
        y: height - 50,
        size: 24,
        font,
        color: rgb(0.49, 0.23, 0.93),
      });

      // Order Details
      page.drawText(`Order Receipt`, {
        x: 50,
        y: height - 120,
        size: 16,
        font,
      });

      page.drawText(`Order ID: ${order.id}`, {
        x: 50,
        y: height - 140,
        size: 12,
        font: regularFont,
      });

      page.drawText(`Date: ${new Date(order.orderDate).toLocaleDateString()}`, {
        x: 50,
        y: height - 160,
        size: 12,
        font: regularFont,
      });

      // Items
      let currentY = height - 200;
      order.items.forEach((item) => {
        page.drawText(item.name, {
          x: 50,
          y: currentY,
          size: 10,
          font: regularFont,
        });
        page.drawText(`₹${item.price * item.quantity}`, {
          x: 500,
          y: currentY,
          size: 10,
          font: regularFont,
        });
        currentY -= 20;
      });

      // Total
      page.drawText(`Total Amount: ₹${order.totalAmount}`, {
        x: 400,
        y: currentY - 40,
        size: 12,
        font,
      });

      // Save and download
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `order-receipt-${order.id}.pdf`;
      a.click();
    } catch (error) {
      console.error('Error generating receipt:', error);
      alert('Failed to generate receipt');
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'delivered': 'bg-green-100 text-green-800',
      'shipped': 'bg-purple-100 text-purple-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status.toLowerCase()] || 'bg-purple-100 text-purple-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-medium text-purple-900">My Orders</h3>
        <div className="flex space-x-2">
          <select 
            className="px-4 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Orders</option>
            <option value="delivered">Delivered</option>
            <option value="shipped">Shipped</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-purple-600">No orders found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-white border border-purple-200 rounded-lg overflow-hidden shadow-sm">
              <div 
                className="p-6 cursor-pointer hover:bg-purple-50 transition-colors duration-200"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <img
                      src={order.items[0]?.images[0]}
                      alt={order.items[0]?.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="space-y-1">
                      <h4 className="font-medium text-purple-900">
                        {order.items[0]?.name}
                        {order.items.length > 1 && ` +${order.items.length - 1} more`}
                      </h4>
                      <div className="flex items-center space-x-2 text-sm text-purple-500">
                        <Calendar className="w-4 h-4" />
                        <span>Ordered on {new Date(order.orderDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                    <span className="font-medium text-purple-900">₹{order.totalAmount}</span>
                    {expandedOrder === order.id ? (
                      <ChevronUp className="w-5 h-5 text-purple-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-purple-400" />
                    )}
                  </div>
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="px-6 pb-6 border-t border-purple-200">
                  <div className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <Truck className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-600">Delivery Status:</span>
                        <span className="font-medium text-purple-900">{order.status}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <CreditCard className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-600">Payment Method:</span>
                        <span className="font-medium text-purple-900">
                          {order.paymentMethod === 'online' ? 'Online Payment' : 'Cash on Delivery'}
                        </span>
                      </div>
                      {order.paymentId && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Package className="w-4 h-4 text-purple-400" />
                          <span className="text-purple-600">Payment ID:</span>
                          <span className="font-medium text-purple-900">{order.paymentId}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                      <div className="text-sm">
                        <span className="text-purple-600">Shipping Address:</span>
                        <p className="font-medium text-purple-900 mt-1">{order.address.detail}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="mt-6">
                    <h5 className="font-medium text-purple-900 mb-3">Order Items</h5>
                    <div className="space-y-3">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-purple-100">
                          <div className="flex items-center space-x-3">
                            <img
                              src={item.images[0]}
                              alt={item.name}
                              className="w-12 h-12 rounded object-cover"
                            />
                            <div>
                              <p className="font-medium text-purple-900">{item.name}</p>
                              <p className="text-sm text-purple-600">Quantity: {item.quantity}</p>
                            </div>
                          </div>
                          <span className="font-medium text-purple-900">₹{item.price * item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-4 mt-6">
                    <button
                      onClick={() => generateReceipt(order)}
                      className="flex items-center space-x-2 px-4 py-2 border border-purple-200 text-sm font-medium text-purple-700 rounded-lg hover:bg-purple-50 transition-all duration-200 transform hover:scale-105"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Invoice</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOrders;